import {animate, state, style, transition, trigger} from "@angular/animations";
import {SelectionModel} from "@angular/cdk/collections";
import {AsyncPipe, NgIf} from "@angular/common";
import {Component, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {FlexModule} from "@angular/flex-layout";
import {MatButtonModule} from "@angular/material/button";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MatDialog} from "@angular/material/dialog";
import {MatIconModule} from "@angular/material/icon";
import {MatSortModule} from "@angular/material/sort";
import {MatTableDataSource, MatTableModule} from "@angular/material/table";
import {PageOffering} from "@core/model/side-nav.model";
import {PatternType} from "@features/leasing/lease-bookings/bookings.model";
import {LeaseBookingsService} from "@features/leasing/lease-bookings/lease-bookings.service";
import {ManageBookingComponent} from "@features/leasing/lease-bookings/manage-booking/manage-booking.component";
import {
  ManageSubleaseBookingComponent
} from "@features/leasing/lease-bookings/manage-sublease-booking/manage-sublease-booking.component";
import {
  EditPricingPeriodDialogComponent
} from "@features/leasing/lease-view/lease-space-rates/edit-pricing-period-dialog/edit-pricing-period-dialog.component";
import {
  LeaseSpacesTableComponent
} from "@features/leasing/lease-view/lease-space-rates/lease-spaces-table/lease-spaces-table.component";
import {LoadingService} from "../../../../../../../nter-ui-kit/src/lib/framework/util/service/loading.service";
import {AppSharedModule} from "../../../../core/modules/app-shared.module";
import {PanelType} from "../../../../shared/components/info-panel/info-panel.component";
import {DateUtilsService} from "../../../../shared/services/dateUtils.service";
import {UtilsService} from "../../../../shared/services/utils.service";
import {LeasePricingPeriod, LeaseSummary, OccupancyType, PaymentMethod, RentalPricingOption} from "../../leasing.model";
import {LeasingService} from "../../leasing.service";
import {LeaseViewService} from "../lease-view.service";

@Component({
  selector: 'app-lease-space-rates',
  standalone: true,
  imports: [
    AppSharedModule,
    AsyncPipe,
    FlexModule,
    MatTableModule,
    MatButtonModule,
    NgIf,
    MatCheckboxModule,
    MatIconModule,
    LeaseSpacesTableComponent,
    ManageBookingComponent,
    MatSortModule,
    ManageSubleaseBookingComponent
  ],
  templateUrl: './lease-space-rates.component.html',
  styleUrl: './lease-space-rates.component.scss',
  animations: [
    trigger('detailExpand', [
      state('collapsed,void', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class LeaseSpaceRatesComponent {
  @Input() id!: string;
  @Input() offering!: PageOffering;
  @Input() showRates: boolean = true;
  @Input() isSubLease: boolean = false;
  @Output() reloadLease = new EventEmitter<void>();

  @ViewChild('spacesTable') spacesTable?: LeaseSpacesTableComponent;

  leaseSummary: LeaseSummary | null = null;
  paymentMethods: PaymentMethod[] = [];
  isAddingTransactionSource: boolean = false;
  leasePricingPeriodDataSource: MatTableDataSource<LeasePricingPeriod> = new MatTableDataSource();
  pricingPeriodDisplayedColumns: string[] = ['select', 'uuid', 'rentalPricingOption', 'includedDays', 'fixedRentalPricingValue', 'rentalPricingTurnoverPercentageValue', 'applyTransactionFee', 'startDate', 'endDate', 'expand']
  pricingPeriodSelection = new SelectionModel<LeasePricingPeriod>(false);
  selectedPricingPeriodUuid: string | null = null;

  expandedElement = new SelectionModel<LeasePricingPeriod>(false, []);
  hoveredElement: any = null;

  editingLease: boolean = false;
  addingToLease: boolean = false;
  removePanelOpen: boolean = false;

  parentLease: LeaseSummary | null = null;

  constructor(public utils: UtilsService,
              private leaseViewService: LeaseViewService,
              public loader: LoadingService,
              public dateUtils: DateUtilsService,
              private bookingsService: LeaseBookingsService,
              private matDialog: MatDialog,
              private leasingService: LeasingService) {
  }

  ngOnInit() {
    this.loadPaymentMethods();
    this.loadLeasePricingPeriods();
    this.loadLease();
  }

  /** Checks whether an element is expanded. */
  isExpanded(element: LeasePricingPeriod) {
    return this.expandedElement.isSelected(element);
  }

  /** Toggles the expanded state of an element. */
  toggle(element: LeasePricingPeriod) {
    this.expandedElement.toggle(element);
  }

  loadLease(): void {
    this.leaseViewService.getOne('', this.id).subscribe(
      {
        next: value => {
          this.leaseSummary = value;
          if (this.isSubLease) this.loadParentLease();
        },
        error: error => {
          console.log(error);
        }
      }
    )
  }

  loadLeasePricingPeriods() {
    this.leaseViewService.getAllLeasePricingPeriods(this.id).subscribe({
      next: value => {
        const sorted = [...value].sort((a, b) => {
          const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
          const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
          return dateA - dateB;
        });
        this.leasePricingPeriodDataSource.data = [...sorted];
        this.expandedElement.setSelection(...sorted);
      },
      error: err => {
        console.log(err);
      }
    });
  }

  loadPaymentMethods() {
    this.leaseViewService.getPaymentMethods(this.id).subscribe({
      next: value => {
        this.paymentMethods = value;
      },
      error: err => console.log(err)
    })
  }

  loadParentLease() {
    this.leaseViewService.getOne('', this.leaseSummary!.parentUuid!).subscribe({
      next: value => {
        this.parentLease = value;
      },
      error: err => {
        console.log(err);
      }
    })
  }

  displayDate(date: any): string {
    if (typeof date === 'number') {
      let dateUnix: string | null = this.dateUtils.displayShortDateUnix(date);
      return dateUnix ? dateUnix : '';
    } else if (date instanceof Date) {
      let shortDate: string | null = this.dateUtils.displayShortDate(date);
      return shortDate ? shortDate : '';
    }
    return '';
  }

  cancel() {
    this.isAddingTransactionSource = false;
  }

  protected readonly PanelType = PanelType;
  protected readonly OccupancyType = OccupancyType;

  setSelectedPricingPeriod(row: LeasePricingPeriod): void {
    this.selectedPricingPeriodUuid = row.uuid;
    this.pricingPeriodSelection.toggle(row);

    if (this.pricingPeriodSelection.selected.length == 0) this.selectedPricingPeriodUuid = null;
  }

  disablePricingPeriodEdit(period: LeasePricingPeriod, disableEdit: boolean): void {
    period.isEditable = !disableEdit;
  }

  removePricingPeriod(remove: boolean): void {
    if (remove) {
      if (this.isSubLease) {
        if (this.selectedPricingPeriodUuid != null) {
          this.bookingsService.removeSubLeaseBookingPeriod(this.leaseSummary!.uuid, this.selectedPricingPeriodUuid!)
            .subscribe({
              next: () => {
                this.loadLeasePricingPeriods();
                this.reloadLeaseSummary();
                this.removePanelOpen = false;
                this.selectedPricingPeriodUuid = null;
              },
              error: err => console.log(err)
            })
        }
      } else {
        if (this.selectedPricingPeriodUuid != null) {
          this.bookingsService.removeBookingPeriod(this.leaseSummary!.uuid, this.selectedPricingPeriodUuid!)
            .subscribe({
              next: () => {
                this.loadLeasePricingPeriods();
                this.reloadLeaseSummary();
                this.removePanelOpen = false;
                this.selectedPricingPeriodUuid = null;
              },
              error: err => console.log(err)
            })
        }
      }
    } else {
      this.removePanelOpen = false;
    }
  }

  addLeaseSpaceAndPricing() {
    this.addingToLease = true
  }

  editApplicationSpaceAndPricing() {
    this.editingLease = true;
  }

  cancelLeaseEdit() {
    this.editingLease = false;
    this.addingToLease = false;
  }

  editLeasePricingPeriod(type: 'values' | 'dates'): void {
    this.matDialog.open(EditPricingPeriodDialogComponent, {
      panelClass: type == 'values' ? 'dialog-xl' : 'dialog-large',
      data: {
        isEditing: true,
        header: type == 'values' ? 'Edit an existing pricing period' : 'Shorten an existing pricing period',
        allPeriods: this.leasePricingPeriodDataSource.data,
        selectedPeriod: this.pricingPeriodSelection.selected[0],
        leaseId: this.id,
        leaseStartDate: this.leaseSummary?.leaseStartDate,
        leaseEndDate: this.leaseSummary?.leaseEndDate,
        paymentMethods: this.paymentMethods,
        editType: type
      }
    }).afterClosed().subscribe((response: { save: boolean, periodToSave: LeasePricingPeriod }) => {
      if (response.save) {
        if (type == 'values') {
          this.updateLeasePeriodValues(response.periodToSave);
        }
        if (type == 'dates') {
          this.shortenLeasePeriod(response.periodToSave);
        }
      }
    });
  }

  reloadLeaseSpaceAndPricing(reload: boolean) {
    if (reload) {
      this.editingLease = false;
      this.addingToLease = false;
      this.reloadLeaseSummary();
      this.loadLeasePricingPeriods();
      if (this.spacesTable) {
        this.spacesTable.loadLease();
      }
    }
  }

  reloadLeaseSummary() {
    this.loadLease();
    this.reloadLease.emit();
  }

  shortenLeasePeriod(pricingPeriod: LeasePricingPeriod) {
    if (this.isSubLease) {
      this.bookingsService.shortenSubLeaseBookingPeriod(this.leaseSummary!.uuid, pricingPeriod)
        .subscribe({
          next: () => {
            this.loadLeasePricingPeriods();
            this.reloadLeaseSummary();
          },
          error: err => console.log(err)
        })
    } else {
      this.bookingsService.shortenBookingPeriod(this.leaseSummary!.uuid, pricingPeriod)
        .subscribe({
          next: () => {
            this.loadLeasePricingPeriods();
            this.reloadLeaseSummary();
          },
          error: err => console.log(err)
        })
    }
  }

  updateLeasePeriodValues(pricingPeriod: LeasePricingPeriod) {
    this.leaseViewService.updateLeasePricingPeriod(this.id, pricingPeriod).subscribe({
      next: () => {
        this.loadLeasePricingPeriods();
        this.reloadLeaseSummary();
      },
      error: err => console.log(err)
    });
  }

  getRentalType(typeCode: RentalPricingOption | null, pricingPeriod: LeasePricingPeriod): string {
    return this.leasingService.getRentalType(typeCode, pricingPeriod.bookingPricing ?? null);
  }

  getIncludedDays(pricingPeriod: LeasePricingPeriod): string {
    if (!pricingPeriod.bookingPricing) return 'All days';

    switch (pricingPeriod.bookingPricing.patternType) {
      case PatternType.DAILY_CUSTOM:
        return 'Custom';
      case PatternType.WEEKENDS:
        if (pricingPeriod.bookingPricing.includeDays.includes('Friday')) return "Fr, Sa, Su";
        return "Sa, Su";
      case PatternType.WEEKLY_CUSTOM:
        return pricingPeriod.bookingPricing.includeDays
          .map(m => m.slice(0, 2))
          .join(', ');
      default:
        return 'All Days';
    }
  }

  showAddButton() {
    return this.leaseSummary?.leaseType != 'APPLICATION'
      && !(this.editingLease || this.addingToLease)
      && !this.pricingPeriodSelection.hasValue();
  }

  showRemoveButton() {
    if (this.leaseSummary?.leaseType == 'APPLICATION') return false;
    return this.pricingPeriodSelection.hasValue()
      && !(this.editingLease || this.addingToLease);
  }

  showEditButton() {
    return this.leaseSummary?.leaseType == 'APPLICATION'
      && !(this.editingLease || this.addingToLease);
  }

  showEditRatesButton() {
    return this.leaseSummary?.leaseType == 'LEASE'
      && this.pricingPeriodSelection.hasValue()
      && !(this.editingLease || this.addingToLease);
  }

  showEditDatesButton() {
    return this.leaseSummary?.leaseType == 'LEASE'
      && this.pricingPeriodSelection.hasValue()
      && !(this.editingLease || this.addingToLease);
  }

  disableEditButton() {
    if (this.leaseSummary?.leaseType == 'APPLICATION') return false;
    return !this.pricingPeriodSelection.selected[0].isEditable;
  }

  disableEditRatesButton() {
    if (this.leaseSummary?.leaseType == 'APPLICATION') return false;
    return !this.pricingPeriodSelection.selected[0].isEditable;
  }

  disableEditDatesButton() {
    return this.leaseSummary?.leaseType == 'APPLICATION';
  }

  disableRemoveButton() {
    return !this.pricingPeriodSelection.selected[0].isEditable
      || this.leasePricingPeriodDataSource.data.length == 1;
  }

  setHovered(element: any): void {
    this.hoveredElement = element;
  }

  clearHovered(): void {
    this.hoveredElement = null;
  }

  isHovered(element: any): boolean {
    return this.hoveredElement === element;
  }
}
